import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Locale, enUS, ja } from "date-fns/locale";
import { DATE_TIME_FORMAT_JP, MIN_DATE_REPORT_SUMMARY } from "@app/constants";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { getMaxDate, isMobile } from "@app/helpers";
import { getUser } from "@app/api";

interface LocalesMap {
  [key: string]: Locale;
}

const locales: LocalesMap = {
  en: enUS,
  ja,
};

export interface DatePickerInputExcelProps {
  label: string;
  value: Date;
  onChange: (newValue: any) => void;
  onAccept: (newValue: any) => void;
  minDate?: Date;
  maxDate?: Date;
}

export const DatePickerExcelInput: FC<DatePickerInputExcelProps> = ({
  label,
  value,
  onChange,
  onAccept,
  minDate,
  maxDate,
}: DatePickerInputExcelProps) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const locale = locales[i18n?.language];
  const calendarInputRef = useRef<HTMLInputElement>(null);
  const [userTimeFormat, setUserTimeFormat] = useState<string>("yyyy/MM/dd");
  const [userTimeFormat1, setUserTimeFormat1] = useState<string>("yyyy/MM/dd");
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        const timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";

        setUserTimeFormat(timeFormat);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        let timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";

        const formatsToModify = ["yyyy/MM/dd", "MM/dd/yyyy", "dd/MM/yyyy"];
        if (formatsToModify.includes(timeFormat)) {
          timeFormat = timeFormat.replace(/^dd\//, "").replace(/\/dd/, "");
        }

        setUserTimeFormat1(timeFormat);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <div className="input-datePicker">
        <DatePicker
          inputRef={calendarInputRef}
          label={label}
          value={value}
          onChange={isMobile() ? undefined : onChange}
          onAccept={isMobile() ? onAccept : undefined}
          slots={{
            openPickerIcon: CalendarMonthOutlinedIcon,
          }}
          slotProps={{
            textField: {
              InputLabelProps: {
                sx: isRTL
                  ? {
                      direction: "rtl",
                      transformOrigin: "top right",
                    }
                  : undefined,
              },
              inputProps: {
                dir: "ltr",
                style: isRTL ? { textAlign: "right" } : undefined,
              },
            },
            toolbar: {
              toolbarFormat:
                locale === locales.ja ? DATE_TIME_FORMAT_JP : undefined,
            },
            calendarHeader: {
              format: userTimeFormat1,
            },
            field: {
              readOnly: true,
            },
            dialog: {
              sx: {
                "& .MuiPaper-root": {
                  minWidth: "unset",
                  width: "unset",
                },
                "& .MuiPickersCalendarHeader-label": {
                  fontWeight: 700,
                },
                "& .MuiTypography-overline": {
                  color: "#fff",
                },
                "& .MuiPickersLayout-contentWrapper": {
                  borderTop: "1px solid #e4e4e4",
                },
                "& .MuiDialogActions-root .MuiButton-root": {
                  textTransform: "none",
                },
              },
            },
          }}
          disableFuture
          minDate={minDate ?? new Date(MIN_DATE_REPORT_SUMMARY)}
          maxDate={maxDate ?? getMaxDate()}
          format={userTimeFormat}
        />

        <CalendarMonthOutlinedIcon
          onClick={() => {
            calendarInputRef.current?.click();
          }}
          className="d-none-imp icon-calendar"
        />
      </div>
    </LocalizationProvider>
  );
};
